import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { Eye, EyeOff, ArrowLeft, Sparkles, ShieldCheck, Zap, Brain } from 'lucide-react';
import { toast } from 'sonner';
import logo from '../assets/NTZLOGOnew.png';
import ReCAPTCHA from 'react-google-recaptcha';
import { loginSchema, validate } from '../lib/validation';

const RECAPTCHA_SITE_KEY = '6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI';

const FEATURES = [
  { icon: Brain, text: '30+ Specialized AI Agents' },
  { icon: ShieldCheck, text: 'SOC2 & HIPAA Compliant' },
  { icon: Zap, text: '99.9% Uptime SLA' },
];

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [visible, setVisible] = useState(false);
  const [captchaToken, setCaptchaToken] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const recaptchaRef = useRef(null);
  const { login, isAuthenticated, loading: authLoading } = useAuth();
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const redirectTo = location.state?.from || '/dashboard';

  useEffect(() => { const t = setTimeout(() => setVisible(true), 60); return () => clearTimeout(t); }, []);

  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      navigate(redirectTo, { replace: true });
    }
  }, [authLoading, isAuthenticated, navigate, redirectTo]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFieldErrors({});
    const result = validate(loginSchema, { email, password });
    if (!result.success) {
      setFieldErrors(result.errors);
      const first = Object.values(result.errors)[0];
      if (first) toast.error(first);
      return;
    }
    if (!captchaToken) { toast.error('Please complete the CAPTCHA'); return; }
    setLoading(true);
    try {
      await login(email, password);
      toast.success('Welcome back!');
      navigate(redirectTo);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
      recaptchaRef.current?.reset();
      setCaptchaToken(null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* ── Left: Form Panel ─────────────────────────────────── */}
      <div className="w-full lg:w-1/2 flex flex-col justify-center px-6 sm:px-8 md:px-14 lg:px-20 py-10 lg:py-0 min-h-screen overflow-y-auto" style={{ background: isDark ? '#0a0f1e' : '#fff' }}>
        {/* Back link */}
        <Link
          to="/"
          className="flex items-center gap-2 mb-12 text-sm hover:text-slate-800 transition-colors group w-fit"
          style={{
            color: isDark ? '#8b949e' : '#64748b',
            opacity: visible ? 1 : 0,
            transform: visible ? 'translateY(0)' : 'translateY(-10px)',
            transition: 'opacity 0.4s ease, transform 0.4s ease',
          }}
        >
          <ArrowLeft className="w-4 h-4 transition-transform duration-200 group-hover:-translate-x-1" />
          Back to Home
        </Link>

        <div
          className="max-w-sm w-full mx-auto"
          style={{
            opacity: visible ? 1 : 0,
            transform: visible ? 'translateY(0)' : 'translateY(20px)',
            transition: 'opacity 0.5s ease 0.05s, transform 0.5s ease 0.05s',
          }}
        >
          {/* Brand mark */}
          <div className="flex items-center gap-2 mb-8">
            <div
              className="w-8 h-8 rounded-lg flex items-center justify-center"
              style={{ background: 'linear-gradient(135deg,#F87217,#FF9A4E)' }}
            >
              <Sparkles className="w-4 h-4 text-white" />
            </div>
            <span className="text-lg font-bold" style={{ color: isDark ? '#f0f6fc' : '#0f172a' }}>Multi-Agentic</span>
          </div>

          <h1 className="text-3xl font-bold mb-2" style={{ color: isDark ? '#f0f6fc' : '#0f172a', fontFamily: "'Outfit', sans-serif", letterSpacing: '-0.02em' }}>
            Welcome back
          </h1>
          <p className="mb-9 text-sm" style={{ color: isDark ? '#8b949e' : '#64748b' }}>Sign in to access your AI agents</p>

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Email */}
            <div className="space-y-1.5">
              <label htmlFor="email" className="block text-sm font-semibold" style={{ color: isDark ? '#c9d1d9' : '#334155' }}>Email</label>
              <input
                id="email"
                type="email"
                placeholder="you@company.com"
                value={email}
                onChange={e => setEmail(e.target.value)}
                className="w-full h-12 px-4 rounded-xl text-sm outline-none transition-all duration-200"
                style={{
                  border: isDark ? '1.5px solid rgba(100,160,255,0.18)' : '1.5px solid rgba(0,0,0,0.1)',
                  background: isDark ? '#0f1729' : '#fafafa',
                  color: isDark ? '#f0f6fc' : '#0f172a',
                }}
                onFocus={e => {
                  e.target.style.border = '1.5px solid #F87217';
                  e.target.style.boxShadow = '0 0 0 3px rgba(248,114,23,0.1)';
                  e.target.style.background = isDark ? '#162040' : '#fff';
                }}
                onBlur={e => {
                  e.target.style.border = isDark ? '1.5px solid rgba(100,160,255,0.18)' : '1.5px solid rgba(0,0,0,0.1)';
                  e.target.style.boxShadow = 'none';
                  e.target.style.background = isDark ? '#0f1729' : '#fafafa';
                }}
                data-testid="login-email-input"
              />
              {fieldErrors.email && <p className="text-xs mt-1" style={{ color: '#ef4444' }}>{fieldErrors.email}</p>}
            </div>

            {/* Password */}
            <div className="space-y-1.5">
              <label htmlFor="password" className="block text-sm font-semibold" style={{ color: isDark ? '#c9d1d9' : '#334155' }}>Password</label>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  className="w-full h-12 px-4 pr-12 rounded-xl text-sm outline-none transition-all duration-200"
                  style={{
                    border: isDark ? '1.5px solid rgba(100,160,255,0.18)' : '1.5px solid rgba(0,0,0,0.1)',
                    background: isDark ? '#0f1729' : '#fafafa',
                    color: isDark ? '#f0f6fc' : '#0f172a',
                  }}
                  onFocus={e => {
                    e.target.style.border = '1.5px solid #F87217';
                    e.target.style.boxShadow = '0 0 0 3px rgba(248,114,23,0.1)';
                    e.target.style.background = isDark ? '#162040' : '#fff';
                  }}
                  onBlur={e => {
                    e.target.style.border = isDark ? '1.5px solid rgba(100,160,255,0.18)' : '1.5px solid rgba(0,0,0,0.1)';
                    e.target.style.boxShadow = 'none';
                    e.target.style.background = isDark ? '#0f1729' : '#fafafa';
                  }}
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700 transition-colors"
                >
                  {showPassword ? <EyeOff className="w-4.5 h-4.5" /> : <Eye className="w-4.5 h-4.5" />}
                </button>
              </div>
              {fieldErrors.password && <p className="text-xs mt-1" style={{ color: '#ef4444' }}>{fieldErrors.password}</p>}
            </div>

            {/* reCAPTCHA */}
            <div className="flex justify-center mt-1" ph-no-capture>
              <ReCAPTCHA
                ref={recaptchaRef}
                sitekey={RECAPTCHA_SITE_KEY}
                onChange={token => setCaptchaToken(token)}
                onExpired={() => setCaptchaToken(null)}
                theme={isDark ? 'dark' : 'light'}
              />
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={loading || !captchaToken}
              className="w-full h-12 rounded-xl text-white font-bold text-sm transition-all duration-200 mt-2 relative overflow-hidden"
              style={{
                background: (loading || !captchaToken) ? '#f0a067' : 'linear-gradient(135deg,#F87217,#FF9A4E)',
                boxShadow: (loading || !captchaToken) ? 'none' : '0 6px 20px rgba(248,114,23,0.35)',
                cursor: !captchaToken ? 'not-allowed' : 'pointer',
              }}
              onMouseEnter={e => { if (!loading && captchaToken) { e.currentTarget.style.boxShadow = '0 10px 28px rgba(248,114,23,0.5)'; e.currentTarget.style.transform = 'translateY(-1px)'; } }}
              onMouseLeave={e => { e.currentTarget.style.boxShadow = (loading || !captchaToken) ? 'none' : '0 6px 20px rgba(248,114,23,0.35)'; e.currentTarget.style.transform = 'translateY(0)'; }}
              data-testid="login-submit-btn"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                  </svg>
                  Signing in...
                </span>
              ) : 'Sign In'}
            </button>
          </form>

          <p className="mt-7 text-center text-sm text-slate-500">
            Don't have an account?{' '}
            <Link to="/register" className="text-[#F87217] font-semibold hover:text-[#C65B12] transition-colors">
              Create one
            </Link>
          </p>
        </div>
      </div>

      {/* ── Right: Visual Panel ───────────────────────────────── */}
      <div
        className="hidden lg:flex w-1/2 relative overflow-hidden items-center justify-center"
        style={{
          background: 'linear-gradient(150deg,#0d1a3a 0%,#1a0a00 50%,#0d1a3a 100%)',
        }}
      >
        {/* Ambient glows */}
        <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(ellipse at 40% 50%, rgba(248,114,23,0.2) 0%, transparent 55%)' }} />
        <div className="absolute top-0 right-0 w-80 h-80 rounded-full pointer-events-none" style={{ background: 'radial-gradient(circle,rgba(139,92,246,0.1) 0%,transparent 65%)', transform: 'translate(30%,-30%)' }} />

        {/* Grid lines */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage: 'linear-gradient(rgba(255,255,255,0.025) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,0.025) 1px,transparent 1px)',
            backgroundSize: '40px 40px',
          }}
        />

        <div className="relative z-10 max-w-xs text-center px-8">
          {/* Central glowing icon */}
          <div
            className="mx-auto mb-8 flex justify-center"
            style={{
              animation: 'float 5s ease-in-out infinite',
            }}
          >
            <img
              src={logo}
              alt="Logo"
              className="w-50 h-50 object-contain"
            />
          </div>

          <h2 className="text-2xl font-bold text-white mb-3" style={{ fontFamily: "'Outfit', sans-serif" }}>
            AI-Powered Enterprise Solutions
          </h2>


          <div className="space-y-3">
            {FEATURES.map(({ icon: Icon, text }, i) => (
              <div
                key={i}
                className="flex items-center gap-3 px-4 py-3 rounded-xl text-left"
                style={{
                  background: 'rgba(255,255,255,0.04)',
                  border: '1px solid rgba(255,255,255,0.07)',
                  animation: `fadeUp 0.5s ease ${0.2 + i * 0.1}s both`,
                }}
              >
                <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(248,114,23,0.15)' }}>
                  <Icon className="w-4 h-4 text-[#F87217]" />
                </div>
                <span className="text-sm text-slate-300 font-medium">{text}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
